import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xxtract/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CategoryCard, CategoryCardSkeleton } from '@/features/completeness/components/CategoryCard';
import { ScoreRing } from '@/features/completeness/components/ScoreRing';
import type { BrandOption } from '@/features/completeness/completeness.api';
import { useDashboardBrands, useDashboardCompleteness } from '../hooks/useDashboard';
import { formatBrandDisplayName } from './brand-display';

interface CompletenessBlockProps {
  gln: string;
  selectedBrandId: string;
  onBrandChange: (brandId: string) => void;
}

function formatCalculatedAt(value: string, language: string) {
  return new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(new Date(value));
}

function BrandOverview({ brands, buildProductUrl }: { brands: BrandOption[]; buildProductUrl: (filters: { brandId?: string; categoryId?: string }) => string }) {
  const { t } = useTranslation();

  if (brands.length === 0) return null;

  return (
    <div className="border-t border-[var(--border)] pt-4">
      <p className="mb-3 text-sm font-medium text-[var(--foreground)]">{t('dashboard.completeness.byBrand')}</p>
      <ul className="space-y-2">
        {brands.map((brand) => {
          const brandLabel = formatBrandDisplayName(brand, t);
          return (
            <li key={brand.id}>
              <Link
                aria-label={brandLabel}
                className="flex min-h-11 items-center justify-between rounded-[var(--radius-control)] px-3 text-sm text-[var(--foreground)] hover:bg-[var(--background-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
                to={buildProductUrl({ brandId: brand.id })}
              >
                <span>{brandLabel}</span>
                <span className="text-[var(--foreground-muted)]">
                  {t('dashboard.completeness.brandScore', { score: brand.score, count: brand.productCount })}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface BrandSelectorProps {
  brands: BrandOption[];
  isError: boolean;
  isLoading: boolean;
  onBrandChange: (brandId: string) => void;
  onRetry: () => void;
  selectedBrandId: string;
}

function BrandSelector({ brands, isError, isLoading, onBrandChange, onRetry, selectedBrandId }: BrandSelectorProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return <div aria-live="polite" className="min-h-11 text-sm text-[var(--foreground-muted)]" data-testid="dashboard-brands-loading">{t('dashboard.completeness.brandsLoading')}</div>;
  }

  if (isError) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-brands-unavailable" role="status">
        <span className="text-sm text-[var(--foreground-muted)]">{t('dashboard.completeness.brandsUnavailableDescription')}</span>
        <Button className="min-h-11" onClick={onRetry} variant="secondary">{t('common.retry')}</Button>
      </div>
    );
  }

  if (brands.length === 0) {
    return <p className="min-h-11 text-sm text-[var(--foreground-muted)]" data-testid="dashboard-brands-empty">{t('dashboard.completeness.brandsEmpty')}</p>;
  }

  return (
    <label className="flex min-h-11 items-center gap-2 text-sm text-[var(--foreground)]" htmlFor="dashboard-brand-selector">
      <span>{t('dashboard.completeness.brandLabel')}</span>
      <select
        className="min-h-11 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
        id="dashboard-brand-selector"
        onChange={(event) => onBrandChange(event.target.value)}
        value={selectedBrandId}
      >
        <option value="">{t('dashboard.completeness.allBrands')}</option>
        {brands.map((brand) => {
          const brandLabel = formatBrandDisplayName(brand, t);
          return <option aria-label={brandLabel} key={brand.id} value={brand.id}>{brandLabel}</option>;
        })}
      </select>
    </label>
  );
}

export function CompletenessBlock({ gln, selectedBrandId, onBrandChange }: CompletenessBlockProps) {
  const { t, i18n } = useTranslation();
  const completeness = useDashboardCompleteness(gln, selectedBrandId);
  const brands = useDashboardBrands(gln);

  if (completeness.isError) {
    return (
      <Card data-testid="completeness-unavailable" role="status">
        <CardHeader>
          <CardTitle>{t('dashboard.completeness.unavailableTitle')}</CardTitle>
          <CardDescription>{t('dashboard.completeness.unavailableDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="min-h-11" onClick={() => void completeness.refetch()} variant="secondary">
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const dashboard = completeness.data?.data;
  const brandOptions = brands.data?.data ?? [];
  const buildProductUrl = (filters: { brandId?: string; categoryId?: string }) => {
    const params = new URLSearchParams();
    const brandId = filters.brandId ?? selectedBrandId;
    if (brandId) params.set('brandId', brandId);
    if (filters.categoryId) params.set('category', filters.categoryId);
    const query = params.toString();
    return `/products${query ? `?${query}` : ''}`;
  };

  return (
    <Card data-testid="completeness-block">
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle>{t('dashboard.completeness.title')}</CardTitle>
          {dashboard && <CardDescription>{t('dashboard.completeness.calculatedAt', { date: formatCalculatedAt(dashboard.lastUpdated, i18n.language) })}</CardDescription>}
        </div>
        <BrandSelector
          brands={brandOptions}
          isError={brands.isError}
          isLoading={brands.isLoading}
          onBrandChange={onBrandChange}
          onRetry={() => void brands.refetch()}
          selectedBrandId={selectedBrandId}
        />
      </CardHeader>
      <CardContent className="space-y-5">
        {completeness.isLoading || dashboard === undefined ? (
          <div className="space-y-4" data-testid="completeness-loading">
            <ScoreRing buildProductUrl={buildProductUrl} categories={[]} clientId={gln} isLoading overallScore={0} productsPath="/products" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {Array.from({ length: 5 }, (_, index) => <CategoryCardSkeleton key={index} />)}
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-5 lg:flex-row lg:items-start">
              <ScoreRing buildProductUrl={buildProductUrl} categories={dashboard.categories} clientId={gln} overallScore={dashboard.overallScore} productsPath="/products" />
              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {dashboard.categories.map((category) => (
                  <CategoryCard
                    category={category}
                    clientId={gln}
                    completeCount={category.filledCount}
                    incompleteCount={category.incompleteCount}
                    key={category.categoryId}
                    buildProductUrl={buildProductUrl}
                    productsPath="/products"
                    totalCount={category.fieldCount}
                  />
                ))}
              </div>
            </div>
            {!brands.isError && <BrandOverview brands={brandOptions} buildProductUrl={buildProductUrl} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}
